import type { Credit, Household } from '@frontend/api-types';
import { dateFormatters, dayjs, pluralize, sumBy } from '@frontend/duck-tape';

export const getOtherHouseholdMembers = (currentClientId: MaybeNil<string>, household: MaybeNil<Household>) =>
  household?.members?.filter(({ id: hhMemberId }) => hhMemberId !== currentClientId) ?? [];

export const formatAvailableCredits = (credit?: Credit) => {
  if (!credit) return '';
  return `${credit.availableCredits} ${pluralize('credit', credit.availableCredits)}`;
};

export const areCreditsExpiringSoon = (expirationDate: string, days: number = 7): boolean => {
  const diffDays = dayjs(expirationDate).diff(dayjs(), 'day');
  return diffDays <= days && diffDays >= 0;
};

export const getExpiringSoonCreditsCount = (credits: MaybeUndefined<Credit>, days: number = 7): number => {
  if (!credits?.activeAllocations) return 0;

  return credits.activeAllocations.reduce((total, allocation) => {
    const remainingCredits = allocation.amount - allocation.amountUsed;
    if (remainingCredits > 0 && areCreditsExpiringSoon(allocation.expiration, days)) {
      return total + remainingCredits;
    }
    return total;
  }, 0);
};

export const getCalculatedCreditsData = (credits: MaybeUndefined<Credit>) => {
  if (!credits) return undefined;
  const countMaxUsableCredits = sumBy(credits.activeAllocations, (a) => a.amount);
  const countCreditsUsed = sumBy(credits.activeAllocations, (a) => a.amountUsed);
  const countCreditsRemaining = countMaxUsableCredits - countCreditsUsed;
  return {
    countCreditsRemaining,
    countCreditsUsed,
    countMaxUsableCredits,
    countUpcomingRenewalCredits: credits?.subscriptionRenewalCredits,
    percentUsed: countCreditsUsed / countMaxUsableCredits,
  };
};

export const formatCreditBreakdownEntry = (creditType: string, remainingCredits: number, expirationDate: string) => {
  const formattedCreditType = creditType
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const numberStr = remainingCredits.toString();
  const paddedNumber =
    numberStr.length === 1
      ? '\u00A0\u00A0\u00A0\u00A0' + numberStr
      : numberStr.length === 2
        ? '\u00A0\u00A0' + numberStr
        : numberStr;
  const creditWord = pluralize('credit', remainingCredits);
  const paddedCreditWord = creditWord === 'credits' ? creditWord : creditWord + '\u00A0\u00A0';
  const creditsText = `${paddedNumber}\u00A0\u00A0${paddedCreditWord}`;

  const baseExpirationText = expirationDate ? `Expires ${dateFormatters['M/D'](expirationDate)}` : '';
  const expirationText = baseExpirationText + '\u00A0\u00A0'.repeat(Math.max(0, 12 - baseExpirationText.length));

  return {
    creditType: formattedCreditType,
    credits: creditsText,
    expiration: expirationText,
    paddedCreditWord: paddedCreditWord,
    paddedNumber: paddedNumber,
  };
};

export type MonthlyBandwidthProgressDirection = 'drain' | 'fill';

export const calculateMonthlyBandwidthUsagePercentage = (
  credits: MaybeUndefined<Credit>,
  direction: MonthlyBandwidthProgressDirection = 'drain',
  maxCreditsOverride?: number,
): number => {
  const monthlyRecurring = maxCreditsOverride ?? credits?.subscriptionRenewalCredits ?? 0;

  if (monthlyRecurring <= 0) {
    return direction === 'drain' ? 100 : 0;
  }

  const availableCredits = Math.max(0, credits?.availableCredits ?? 0);

  if (direction === 'drain') {
    return Math.min(100, (availableCredits / monthlyRecurring) * 100);
  } else {
    const cappedAvailable = Math.min(availableCredits, monthlyRecurring);
    return ((monthlyRecurring - cappedAvailable) / monthlyRecurring) * 100;
  }
};

export { creditTypeConfig, type CreditTypeConfig, type CreditTypeIconName } from './creditTypeConfig';
export { getCreditBreakdown, type CreditBreakdownItem, type CreditBreakdownType } from './getCreditBreakdown';
export { getCurrentSubscriptionId } from './getCurrentSubscriptionId';
